/**
 * Initials for the avatar.
 *
 * Clerk always hands back an `imageUrl` — when the user never uploaded a
 * picture it points at Clerk's own generated one — so "no image" is not a
 * state we can fall back from. The avatar is ours instead, and this derives
 * what goes in it.
 */

/** The identity fields we can build initials from. Clerk leaves any of them null. */
export interface InitialsSource {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  username?: string | null;
  email?: string | null;
}

/**
 * Skips punctuation and digits so "J.R." and "user2" give a letter, not a dot.
 *
 * `\p{L}` rather than a case comparison: most of the world's scripts have no
 * case, so testing whether a character changes under `toUpperCase()` would
 * quietly decide that a name written in Chinese or Arabic contains no letters
 * at all and fall through to "?".
 */
const LETTER = /\p{L}/u;

function firstLetter(value: string | null | undefined): string | null {
  if (!value) return null;
  for (const char of value) {
    if (LETTER.test(char)) return char.toUpperCase();
  }
  return null;
}

/**
 * One or two letters, upper case.
 *
 * Prefers the structured first/last name, falls back to splitting a full name,
 * then to the local part of the email, then the username. Returns "?" when the
 * account has no letters anywhere — a blank circle looks broken.
 */
export function initialsFrom(user: InitialsSource | null | undefined): string {
  if (!user) return "?";

  const first = firstLetter(user.firstName);
  const last = firstLetter(user.lastName);
  if (first && last) return first + last;
  if (first) return first;
  if (last) return last;

  // A full name is a single string for accounts created with one field, and
  // for OAuth providers that do not split it.
  const words = (user.fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    const head = firstLetter(words[0]);
    const tail = firstLetter(words[words.length - 1]);
    if (head && tail) return head + tail;
  }
  const fromFullName = firstLetter(words[0]);
  if (fromFullName) return fromFullName;

  // Email before username: "hkom@equinor.com" is more recognisably *you* than
  // a Clerk-generated handle, and the domain is noise either way.
  const fromEmail = firstLetter((user.email ?? "").split("@")[0]);
  if (fromEmail) return fromEmail;

  return firstLetter(user.username) ?? "?";
}
